/* -----------------Globals--------------- */

/* -----------------UI--------------- */
import { Card, Chip, type ChipProps } from "react-native-paper";

/* -----------------Types--------------- */
import type { MixedItemTag } from "../../types";

import Icon from "@expo/vector-icons/MaterialCommunityIcons";
/* -----------------Components--------------- */
import { SectionList } from "react-native";
import type { FontSchemaType } from "../../font";
import type { IconType } from "../../icon";
import { useDevice } from "../../useDevice";
import Box from "../Box";
import { CustomListSubheader } from "../CustomListSubheader";
import { MasteryListItem } from "../MasteryListItem";
export type SectionTag = {
	title: string;
	tags: MixedItemTag[];
	cta?: string | null;
	action?: unknown;
	icon?: string;
	mode: ChipProps["mode"];
};
type Props = {
	sectionTags: SectionTag[];
	handleNavigateToIAP: () => void;
	handleClickListItem: (selectedTagId: number, locked?: boolean) => void;
	appFont?: FontSchemaType;
};

const TagsSectionList = (props: Props) => {
	const { sectionTags, handleClickListItem, appFont } = props;
	const { isMobile } = useDevice();

	const DATA = sectionTags.map(
		({ action, cta, title, tags: data, icon, mode }) => ({
			title,
			cta,
			action,
			data,
			icon,
			mode,
		}),
	);

	const renderSectionHeader = ({
		section: { title, cta, action, icon, mode },
	}: {
		section: {
			title: string;
			cta?: string | null;
			action?: unknown;
			icon?: string;
			mode?: ChipProps["mode"];
		};
	}) => (
		<Box
			justifyContent="space-between"
			display="flex"
			flexDirection="row"
			alignItems="center"
			paddingHorizontal={isMobile ? 16 : 0}
		>
			<CustomListSubheader enableGutters={false} font={appFont}>
				{title}
			</CustomListSubheader>
			{cta ? (
				<Chip
					compact
					mode={mode}
					icon={(props) => <Icon name={icon as IconType} {...props} />}
					onPress={typeof action === "function" ? () => action() : undefined}
				>
					{cta}
				</Chip>
			) : null}
		</Box>
	);

	const renderItem = ({
		item: { title, locked, distribution, id, description },
	}: {
		item: MixedItemTag;
	}) => {
		return (
			<MasteryListItem
				title={title}
				description={description}
				locked={locked}
				distribution={distribution}
				onPress={() => handleClickListItem(id, locked)}
			/>
		);
	};

	const keyExtractor = (item: MixedItemTag, index: number) =>
		`home-sectionlist-${item.id}-${index}`;
	// Parent scrollview causes error, so remove section list and use directly

	return isMobile ? (
		<SectionList
			sections={DATA}
			renderSectionHeader={renderSectionHeader}
			keyExtractor={keyExtractor}
			renderItem={renderItem}
		/>
	) : (
		<Box gap={16} paddingTop={16} paddingBottom={24}>
			{DATA.map((section) => (
				<Box gap={8} key={section.title} paddingHorizontal={2}>
					{renderSectionHeader({ section })}
					<Card>
						{section.data.map(
							({ id, title, locked, distribution, description }) => (
								<MasteryListItem
									key={id}
									title={title}
									description={description}
									locked={locked}
									distribution={distribution}
									onPress={() => handleClickListItem(id, locked)}
								/>
							),
						)}
					</Card>
				</Box>
			))}
		</Box>
	);
};

export default TagsSectionList;
